import React, { useMemo, useState } from "react";
import {
  Box, Chip, MenuItem, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography, alpha, Pagination as MuiPagination,
  Tooltip,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import { useSelector } from "react-redux";
import {
  useGetSignageActivityQuery,
  useGetCompanyQuery,
} from "../../../Api/attoDeskApi";
import { ISignageActivity } from "../../../Api/Interface/api.interface";
import { selectIsSuperAdmin } from "../../../Store/Auth/AuthSelector";
import PageLoader from "../../../Components/Loader/PageLoader";
import {
  NAVY, BORDER, TEXT_PRIMARY, TEXT_SECONDARY,
} from "../../../theme/tokens";

const formatRelative = (iso: string | null): string => {
  if (!iso) return "—";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
};

const ACTION_GROUPS: Record<string, string[]> = {
  user: ["user_created", "user_updated", "user_deleted"],
  device: [
    "device_created", "device_updated", "device_deleted",
    "device_disabled", "device_enabled", "device_reassigned",
    "device_playlist_assigned", "device_playlist_group_assigned",
    "device_registered", "device_online",
  ],
  media: ["media_uploaded", "media_deleted"],
  playlist: [
    "playlist_created", "playlist_updated", "playlist_deleted",
    "playlist_item_added", "playlist_item_removed", "playlist_items_reordered",
  ],
  playlist_group: [
    "playlist_group_created", "playlist_group_updated", "playlist_group_deleted",
    "playlist_added_to_group", "playlist_removed_from_group",
    "playlist_group_item_updated", "playlist_group_reordered",
  ],
  company: ["company_created", "company_updated", "company_deleted"],
};

const actionColor = (action: string): { bg: string; fg: string } => {
  if (action.endsWith("_deleted") || action.endsWith("_disabled") || action.endsWith("_removed_from_group")) {
    return { bg: "#fee2e2", fg: "#991b1b" };
  }
  if (action.endsWith("_created") || action.endsWith("_uploaded") || action.endsWith("_added") || action.endsWith("_added_to_group")) {
    return { bg: "#dcfce7", fg: "#166534" };
  }
  if (action.endsWith("_reordered") || action.endsWith("_assigned") || action.endsWith("_reassigned")) {
    return { bg: "#dbeafe", fg: "#1e40af" };
  }
  return { bg: "#fef3c7", fg: "#92400e" };
};

const formatJson = (v: Record<string, unknown> | null): string =>
  v && Object.keys(v).length > 0 ? JSON.stringify(v, null, 2) : "";

const SignageActivityList: React.FC<{ companyIdFilter?: number; embedded?: boolean }> = ({
  companyIdFilter, embedded = false,
}) => {
  const isSuper = useSelector(selectIsSuperAdmin);
  const [page, setPage] = useState(1);
  const [actionFilter, setActionFilter] = useState<string>("");
  const [companyFilter, setCompanyFilter] = useState<number | "">(companyIdFilter ?? "");

  const args = useMemo(() => ({
    page,
    per_page: 50,
    action: actionFilter || undefined,
    company_id: (companyIdFilter ?? (companyFilter || undefined)) as number | undefined,
  }), [page, actionFilter, companyFilter, companyIdFilter]);
  const { data, isLoading } = useGetSignageActivityQuery(args);
  const rows: ISignageActivity[] = Array.isArray(data?.data) ? (data?.data as unknown as ISignageActivity[]) : [];
  const meta = (data as any)?.meta || { total: 0, per_page: 50, current_page: 1, last_page: 1 };

  const { data: companiesRes } = useGetCompanyQuery(
    { searchText: "", id: "" },
    { skip: !isSuper || !!companyIdFilter }
  );
  const companies: any[] = Array.isArray(companiesRes?.data) ? (companiesRes?.data as any[]) : [];

  if (isLoading) return <PageLoader height="20vh" />;

  return (
    <Box sx={{ p: embedded ? 0 : 3 }}>
      {!embedded && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: `linear-gradient(135deg, ${alpha("#64748b", 0.18)} 0%, ${alpha("#64748b", 0.08)} 100%)`,
            color: "#475569",
          }}>
            <HistoryIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography fontSize={20} fontWeight={800} color={NAVY} letterSpacing="-0.02em">
              Activity Logs
            </Typography>
            <Typography fontSize={12} color={TEXT_SECONDARY}>
              Every CRUD action on devices, media, playlists, groups and users — with before/after values.
            </Typography>
          </Box>
        </Box>
      )}

      <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${BORDER}`, bgcolor: "#fff" }}>
        <Box sx={{ display: "flex", gap: 1.5, p: 2, alignItems: "center", flexWrap: "wrap" }}>
          <TextField select size="small" label="Action"
            value={actionFilter}
            onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}
            sx={{ minWidth: 200, "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13, bgcolor: "#fff" } }}>
            <MenuItem value="">All actions</MenuItem>
            {Object.entries(ACTION_GROUPS).map(([group, actions]) => [
              <MenuItem key={`${group}-header`} disabled sx={{ opacity: 0.6, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                {group.replace("_", " ")}
              </MenuItem>,
              ...actions.map((a) => (
                <MenuItem key={a} value={a} sx={{ fontSize: 13, pl: 3 }}>{a}</MenuItem>
              )),
            ])}
          </TextField>

          {isSuper && !companyIdFilter && (
            <TextField select size="small" label="Company"
              value={companyFilter}
              onChange={(e) => { setCompanyFilter(e.target.value as any); setPage(1); }}
              sx={{ minWidth: 200, "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13, bgcolor: "#fff" } }}>
              <MenuItem value="">All companies</MenuItem>
              {companies.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.companyName}</MenuItem>
              ))}
            </TextField>
          )}
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY }}>
            {meta.total.toLocaleString()} total · page {meta.current_page} of {meta.last_page}
          </Typography>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontSize: 11, fontWeight: 700, color: TEXT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.05em" }}>When</TableCell>
                <TableCell sx={{ fontSize: 11, fontWeight: 700, color: TEXT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.05em" }}>Action</TableCell>
                <TableCell sx={{ fontSize: 11, fontWeight: 700, color: TEXT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.05em" }}>Actor</TableCell>
                {isSuper && !companyIdFilter && (
                  <TableCell sx={{ fontSize: 11, fontWeight: 700, color: TEXT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.05em" }}>Company</TableCell>
                )}
                <TableCell sx={{ fontSize: 11, fontWeight: 700, color: TEXT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.05em" }}>Subject</TableCell>
                <TableCell sx={{ fontSize: 11, fontWeight: 700, color: TEXT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.05em" }}>Changes</TableCell>
                <TableCell sx={{ fontSize: 11, fontWeight: 700, color: TEXT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.05em" }}>IP</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isSuper && !companyIdFilter ? 7 : 6} sx={{ textAlign: "center", py: 6, color: TEXT_SECONDARY }}>
                    No activity yet.
                  </TableCell>
                </TableRow>
              ) : rows.map((row) => {
                const color = actionColor(row.action);
                const subjectShort = row.subject_type
                  ? row.subject_type.split("\\").pop()
                  : "—";
                const oldStr = formatJson(row.old_values);
                const newStr = formatJson(row.new_values);
                return (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ fontSize: 12, color: TEXT_PRIMARY, whiteSpace: "nowrap" }}>
                      <Tooltip title={row.created_at ? new Date(row.created_at).toLocaleString() : ""}>
                        <span>{formatRelative(row.created_at)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={row.action}
                        sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: color.bg, color: color.fg, fontFamily: "monospace" }} />
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, color: TEXT_PRIMARY }}>
                      {row.user?.name || (row.user_id ? `#${row.user_id}` : "system")}
                    </TableCell>
                    {isSuper && !companyIdFilter && (
                      <TableCell sx={{ fontSize: 12, color: TEXT_PRIMARY }}>
                        {row.company?.name || "—"}
                      </TableCell>
                    )}
                    <TableCell sx={{ fontSize: 12, color: TEXT_PRIMARY }}>
                      <Box>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: NAVY }}>{subjectShort}</Typography>
                        {row.subject_id && (
                          <Typography sx={{ fontSize: 10, color: TEXT_SECONDARY }}>#{row.subject_id}</Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {(oldStr || newStr) ? (
                        <Tooltip
                          title={
                            <Box sx={{ maxWidth: 480 }}>
                              {oldStr && (
                                <Box sx={{ mb: 1 }}>
                                  <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#fca5a5" }}>OLD</Typography>
                                  <Box component="pre" sx={{ m: 0, fontSize: 11, whiteSpace: "pre-wrap" }}>{oldStr}</Box>
                                </Box>
                              )}
                              {newStr && (
                                <Box>
                                  <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#86efac" }}>NEW</Typography>
                                  <Box component="pre" sx={{ m: 0, fontSize: 11, whiteSpace: "pre-wrap" }}>{newStr}</Box>
                                </Box>
                              )}
                            </Box>
                          }
                        >
                          <Typography sx={{ fontSize: 12, color: "#3b82f6", cursor: "pointer", fontWeight: 600 }}>
                            View diff
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: 11, color: TEXT_SECONDARY, fontFamily: "monospace" }}>
                      {row.ip_address || "—"}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {meta.last_page > 1 && (
          <Box sx={{ p: 2, borderTop: `1px solid ${BORDER}`, display: "flex", justifyContent: "flex-end" }}>
            <MuiPagination
              count={meta.last_page}
              page={meta.current_page}
              onChange={(_, p) => setPage(p)}
              shape="rounded" color="primary" size="small"
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default SignageActivityList;
